/**
 * BulkYouTubePrepareButton — botón "Preparar YouTube (masivo)".
 *
 * Comportamiento:
 * - Arranca un job async en backend que procesa TODAS las peticiones del evento:
 *   * las que sólo tienen Spotify → se buscan en YouTube con scoring inteligente
 *   * las que tienen youtube_url pero sin MP3 → se descargan
 *   * las que ya tienen audio_file_id → skip (a menos que force_redownload)
 * - UX: chip "Procesando X/Y" mientras dura + polling cada 3s.
 * - Al terminar: toast resumen y (si hay fallos) dialog con el detalle.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Youtube, Loader2, AlertCircle, CheckCircle2, RefreshCw, Search } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import YouTubeCandidatesDialog from './YouTubeCandidatesDialog';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const BulkYouTubePrepareButton = ({ playlistId, peticiones, onSuccess, compact = false }) => {
  const [job, setJob] = useState(null); // { job_id, completed, total, downloaded, failed, skipped, results, status }
  const [starting, setStarting] = useState(false);
  const [failuresOpen, setFailuresOpen] = useState(false);
  const [failures, setFailures] = useState([]);
  const pollRef = useRef(null);
  const jobRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // Nº de peticiones sin MP3 aún (candidatas al bulk)
  const pending = (peticiones || []).filter((p) => !p.audio_file_id).length;
  const already = (peticiones || []).filter((p) => !!p.audio_file_id).length;
  const total = (peticiones || []).length;
  const [confirmForceOpen, setConfirmForceOpen] = useState(false);
  // Petición seleccionada para elegir manualmente el vídeo de YouTube (cuando el
  // pick automático falló o eligió una versión incorrecta).
  const [manualPeticion, setManualPeticion] = useState(null);

  const startJob = async (forceRedownload = false) => {
    if (!playlistId) return;
    if (!forceRedownload && pending === 0) {
      toast.message('Todas las peticiones ya tienen MP3 descargado. Usa "Forzar todas" para re-descargar.');
      return;
    }
    setStarting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/peticiones/bulk-youtube-prepare/start`,
        { playlist_id: playlistId, force_redownload: forceRedownload },
        { headers: authHeaders(), timeout: 20000 },
      );
      if (res.data.status === 'done') {
        toast.message('Nada que descargar: todas las peticiones ya tienen MP3');
        return;
      }
      const initial = {
        job_id: res.data.job_id,
        status: 'running',
        completed: 0,
        total: res.data.total,
        downloaded: 0,
        failed: 0,
        skipped: 0,
        results: [],
      };
      setJob(initial);
      jobRef.current = initial;
      toast.success(
        forceRedownload
          ? `Re-descargando ${res.data.total} peticiones (forzado)…`
          : `Iniciado: descargando ${res.data.total} peticiones en segundo plano`,
      );

      // Polling cada 3s
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(
            `${BACKEND_URL}/api/peticiones/bulk-youtube-prepare/status/${res.data.job_id}`,
            { headers: authHeaders(), timeout: 10000 },
          );
          const data = s.data || {};
          setJob({
            job_id: res.data.job_id,
            status: data.status,
            completed: data.completed || 0,
            total: data.total || 0,
            downloaded: data.downloaded || 0,
            failed: data.failed || 0,
            skipped: data.skipped || 0,
            results: data.results || [],
          });
          jobRef.current = data;

          if (data.status === 'done' || data.status === 'error') {
            stopPolling();
            const dl = data.downloaded || 0;
            const fl = data.failed || 0;
            if (data.status === 'error') {
              toast.error(`Job fallado: ${data.error || 'error desconocido'}`, { duration: 10000 });
            } else if (fl === 0) {
              toast.success(`Descargadas ${dl} peticiones desde YouTube.`, { duration: 6000 });
            } else {
              const fails = (data.results || []).filter((r) => r.status === 'failed');
              setFailures(fails);
              toast.warning(
                `Terminado: ${dl} descargadas, ${fl} fallaron. Toca "Ver fallos" para revisar.`,
                {
                  duration: 12000,
                  action: {
                    label: 'Ver fallos',
                    onClick: () => setFailuresOpen(true),
                  },
                },
              );
            }
            if (typeof onSuccess === 'function') onSuccess(data);
            // Limpiamos el estado activo tras 5s para que reaparezca el botón
            setTimeout(() => setJob(null), 5000);
          }
        } catch (e) {
          // Un 404 pasajero puede ocurrir si el pod que aloja el job aún está inicializando.
          // No paramos el polling salvo error consistente.
          if (e.response && e.response.status === 404) {
            stopPolling();
            toast.error('El job expiró o no se encuentra');
            setJob(null);
          }
        }
      }, 3000);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Error al iniciar la descarga masiva';
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  // Render: si no hay peticiones y no hay job activo → nada
  if (total === 0 && !job) return null;
  // Si TODAS tienen MP3 y no hay job activo → mostramos sólo el botón de "Forzar todas"
  //   (para poder re-descargar MP3 que suenan raros)

  const isRunning = job && job.status === 'running';

  return (
    <>
      {pending > 0 && (
        <Button
          onClick={() => startJob(false)}
          disabled={starting || isRunning}
          size="sm"
          className="bg-red-600 hover:bg-red-700 text-white text-xs"
          title="Buscar automáticamente cada petición en YouTube y descargar los MP3 (job en segundo plano)"
          data-testid="bulk-yt-prepare-btn"
        >
          {isRunning ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              {job.completed}/{job.total}
              {job.failed > 0 && (
                <span className="ml-1 text-yellow-200">({job.failed}⚠)</span>
              )}
            </>
          ) : starting ? (
            <>
              <Loader2 className="w-4 h-4 sm:mr-1 animate-spin" />
              <span className="hidden sm:inline">Iniciando…</span>
            </>
          ) : (
            <>
              <Youtube className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{compact ? 'YT masivo' : `Preparar YT (${pending})`}</span>
            </>
          )}
        </Button>
      )}

      {/* Botón secundario "Forzar todas" — solo cuando hay peticiones YA descargadas
          (útil si algún MP3 suena raro/incompleto y quieres re-bajar de cero). */}
      {already > 0 && !isRunning && !starting && (
        <Button
          onClick={() => setConfirmForceOpen(true)}
          size="sm"
          variant="outline"
          className="border-red-700/60 text-red-300 hover:bg-red-900/30 text-xs"
          title="Re-descargar TODAS las peticiones aunque ya tengan MP3 (útil si algún audio suena raro)"
          data-testid="bulk-yt-force-btn"
        >
          <RefreshCw className="w-3 h-3 sm:mr-1" />
          <span className="hidden sm:inline">{compact ? 'Todas' : `Forzar todas (${total})`}</span>
        </Button>
      )}

      {/* Confirmación antes de forzar re-descarga (es una acción destructiva:
          los AudioFile antiguos quedan huérfanos y se sustituyen). */}
      <AlertDialog open={confirmForceOpen} onOpenChange={setConfirmForceOpen}>
        <AlertDialogContent
          className="bg-slate-900 border-slate-700"
          data-testid="bulk-yt-force-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              ¿Forzar re-descarga de las {total} peticiones?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Se buscarán y descargarán de nuevo TODAS las peticiones desde YouTube,
              incluidas las {already} que ya tienen MP3. Los MP3 antiguos quedarán
              huérfanos y serán reemplazados. Útil si algún audio suena raro o
              incompleto.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
              data-testid="bulk-yt-force-cancel"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => startJob(true)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-yt-force-confirm"
            >
              Sí, forzar re-descarga
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={failuresOpen} onOpenChange={setFailuresOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg" data-testid="bulk-yt-failures-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-400" />
              {failures.length} petición{failures.length === 1 ? '' : 'es'} no se descargaron
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Revísalas manualmente (buscar en YouTube o subir MP3):
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {failures.map((f, idx) => (
              <div
                key={f.peticion_id || idx}
                className="p-2 rounded bg-slate-800/50 border border-slate-700/50 space-y-1"
                data-testid={`bulk-yt-failure-${idx}`}
              >
                <div className="text-white text-sm font-medium truncate">
                  {f.artist ? `${f.artist} — ` : ''}{f.title || 'sin título'}
                </div>
                <div className="text-yellow-400/80 text-xs">
                  {f.reason || 'motivo desconocido'}
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {f.peticion_id && (f.title || f.spotify_id) && (
                    <Button
                      onClick={() => {
                        // Cerramos el dialog de fallos para dejar espacio al de candidatos.
                        setFailuresOpen(false);
                        // Formato aceptado por YouTubeCandidatesDialog.
                        setManualPeticion({
                          id: f.peticion_id,
                          titulo: f.title || '',
                          artista: f.artist || '',
                          spotify_id: f.spotify_id || null,
                        });
                      }}
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 border-cyan-700/60 text-cyan-300 hover:bg-cyan-900/30 text-xs"
                      data-testid={`bulk-yt-failure-manual-${idx}`}
                      title="Ver los 3 mejores candidatos y elegir tú la versión correcta"
                    >
                      <Search className="w-3 h-3 mr-1" />
                      Elegir manualmente
                    </Button>
                  )}
                  {f.youtube_url && (
                    <a
                      href={f.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-400 text-xs hover:underline"
                    >
                      Abrir vídeo en YouTube →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setFailuresOpen(false)}
              className="bg-slate-700 hover:bg-slate-600"
              data-testid="bulk-yt-failures-close"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selector manual de candidatos de YouTube — se abre desde un fallo
          para elegir a mano la versión correcta cuando el pick automático
          falló o no encontró el vídeo. */}
      {manualPeticion && (
        <YouTubeCandidatesDialog
          open={!!manualPeticion}
          onOpenChange={(v) => { if (!v) setManualPeticion(null); }}
          peticion={manualPeticion}
          playlistId={playlistId}
          onSuccess={(res) => {
            // Si la descarga tuvo éxito, quitamos la petición de la lista de fallos.
            if (res && res.success) {
              setFailures((prev) => prev.filter((f) => f.peticion_id !== manualPeticion.id));
              toast.success(`MP3 descargado manualmente: ${manualPeticion.titulo || 'petición'}`);
              if (typeof onSuccess === 'function') onSuccess(res);
            }
            setManualPeticion(null);
          }}
        />
      )}
    </>
  );
};

export default BulkYouTubePrepareButton;
